// src/components/grid/NewLineManagementModal.tsx
// COMPLETE REWRITE: Simple, intuitive line management
import React, { useState, useCallback, useEffect } from 'react';
import { Plus, Trash2, AlertTriangle, Save } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { debug } from '../../utils/debug';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';

interface Line {
  id: string;
  name?: string;
  semester: 'semester1' | 'semester2' | 'fullyear';
  pairedLineId?: string;
  days?: string[];
}

interface Semester {
  id: string;
  name: string;
  lineIds: string[];
}

interface NewLineManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  lines: Line[];
  semesters: Semester[];
  onSaveChanges: (lines: Line[], semesters: Semester[]) => Promise<void>;
  classCount: number;
}

const NewLineManagementModal: React.FC<NewLineManagementModalProps> = ({
  isOpen,
  onClose,
  lines: initialLines,
  semesters: initialSemesters,
  onSaveChanges,
  classCount
}) => {
  const [lines, setLines] = useState<Line[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();
  const { addToast } = useToast();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      debug('🆕 NewLineManagement: Modal opened');
      debug('🆕 NewLineManagement: Initial lines:', initialLines.length);
      setLines([...initialLines]);
      setHasChanges(false);
    }
  }, [isOpen, initialLines]);

  // Track changes
  useEffect(() => {
    if (isOpen) {
      const hasChanged = JSON.stringify(lines) !== JSON.stringify(initialLines);
      setHasChanges(hasChanged);
      debug('🆕 NewLineManagement: Changes detected:', hasChanged);
    }
  }, [lines, initialLines, isOpen]);

  // Generate next available line ID for a semester
  const getNextLineId = (semester: 'semester1' | 'semester2'): string => {
    const existingIds = lines.map(l => l.id);
    
    if (semester === 'semester1') {
      // Try A, B, C, D, E, F, AS1, BS1, CS1, etc.
      const basicIds = ['A', 'B', 'C', 'D', 'E', 'F'];
      for (const id of basicIds) {
        if (!existingIds.includes(id)) return id;
      }
      // If basic IDs are taken, try AS1, BS1, etc.
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      for (let i = 0; i < letters.length; i++) {
        const id = `${letters[i]}S1`;
        if (!existingIds.includes(id)) return id;
      }
    } else {
      // Try G, H, I, J, K, L, AS2, BS2, CS2, etc.
      const basicIds = ['G', 'H', 'I', 'J', 'K', 'L'];
      for (const id of basicIds) {
        if (!existingIds.includes(id)) return id;
      }
      // If basic IDs are taken, try AS2, BS2, etc.
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      for (let i = 0; i < letters.length; i++) {
        const id = `${letters[i]}S2`;
        if (!existingIds.includes(id)) return id;
      }
    }
    
    // Fallback
    return `LINE_${Date.now()}`;
  };

  // Add a new line to specific semester
  const addLine = (semester: 'semester1' | 'semester2') => {
    const newLineId = getNextLineId(semester);
    const newLine: Line = {
      id: newLineId,
      name: `Line ${newLineId}`,
      semester,
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    };
    
    setLines(prev => [...prev, newLine]);
    debug('🆕 NewLineManagement: Added line', newLineId, 'to', semester);
  };

  // Remove a line
  const removeLine = async (lineId: string) => {
    if (classCount > 0) {
      const confirmed = await confirm({
        title: 'Remove line?',
        message:
          `Warning: Removing this line will affect ${classCount} classes. ` +
          `Any teacher assignments to this line will be lost. Continue?`,
        confirmLabel: 'Remove',
        variant: 'danger',
      });
      if (!confirmed) return;
    }
    
    setLines(prev => prev.filter(line => line.id !== lineId));
    debug('🆕 NewLineManagement: Removed line', lineId);
  };

  // Update line name
  const updateLineName = (lineId: string, newName: string) => {
    setLines(prev => prev.map(line => 
      line.id === lineId ? { ...line, name: newName } : line
    ));
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      debug('🆕 NewLineManagement: Saving', lines.length, 'lines');
      
      // Create updated semesters based on line assignments
      const semester1Lines = lines.filter(l => l.semester === 'semester1');
      const semester2Lines = lines.filter(l => l.semester === 'semester2');
      
      const updatedSemesters: Semester[] = [
        {
          id: 'semester1',
          name: 'Semester 1',
          lineIds: semester1Lines.map(l => l.id)
        },
        {
          id: 'semester2', 
          name: 'Semester 2',
          lineIds: semester2Lines.map(l => l.id)
        },
        {
          id: 'fullyear',
          name: 'Full Year',
          lineIds: lines.map(l => l.id) // Full year includes all lines
        }
      ];
      
      debug('🆕 NewLineManagement: Updated semesters:', updatedSemesters);
      await onSaveChanges(lines, updatedSemesters);
      debug('🆕 NewLineManagement: Save successful');
      onClose();
    } catch (error) {
      console.error('🆕 NewLineManagement: Save failed:', error);
      addToast('Failed to save changes. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Separate lines by semester
  const semester1Lines = lines.filter(l => l.semester === 'semester1');
  const semester2Lines = lines.filter(l => l.semester === 'semester2');

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Manage Grid Lines"
      size="lg"
      footer={
        <div className="flex justify-between items-center w-full">
          <div className="text-sm text-ink-500">
            {lines.length} total lines • S1: {semester1Lines.length}, S2: {semester2Lines.length}
            {hasChanges && <span className="text-warn-fg ml-2">• Unsaved changes</span>}
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
              leftIcon={saving ? undefined : <Save className="h-4 w-4" />}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-navy-50 border border-navy-200 rounded-md p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-navy-600 mr-2" />
            <div className="text-sm text-navy-800">
              <div className="font-medium">Line Management</div>
              <div>Semester 1 lines (A-F) run in first half of year. Semester 2 lines (G-L) run in second half.</div>
            </div>
          </div>
        </div>

        {/* Semester 1 Lines */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-navy-700">Semester 1 Lines</h3>
            <Button 
              variant="outline" 
              size="sm" 
              leftIcon={<Plus className="h-4 w-4" />} 
              onClick={() => addLine('semester1')}
            >
              Add S1 Line
            </Button>
          </div>
          
          <div className="grid gap-3">
            {semester1Lines.map(line => (
              <div key={line.id} className="flex items-center gap-3 p-3 bg-navy-50 rounded-lg">
                <div className="font-mono tabular-nums text-sm font-bold text-navy-800 w-12">
                  {line.id}
                </div>
                <input
                  type="text"
                  value={line.name || ''}
                  onChange={(e) => updateLineName(line.id, e.target.value)}
                  placeholder={`Line ${line.id} Name`}
                  className="flex-1 px-2 py-1 border border-navy-200 rounded text-sm"
                />
                <button
                  onClick={() => removeLine(line.id)}
                  className="p-1 text-danger-fg hover:text-danger-fg hover:bg-danger-bg rounded"
                  title="Remove this line"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            
            {semester1Lines.length === 0 && (
              <div className="text-center py-4 text-ink-500">
                No Semester 1 lines. Click "Add S1 Line" to create one.
              </div>
            )}
          </div>
        </div>

        {/* Semester 2 Lines */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-ok-fg">Semester 2 Lines</h3>
            <Button 
              variant="outline" 
              size="sm" 
              leftIcon={<Plus className="h-4 w-4" />} 
              onClick={() => addLine('semester2')}
            >
              Add S2 Line
            </Button>
          </div>
          
          <div className="grid gap-3">
            {semester2Lines.map(line => (
              <div key={line.id} className="flex items-center gap-3 p-3 bg-ok-bg rounded-lg">
                <div className="font-mono tabular-nums text-sm font-bold text-ok-fg w-12">
                  {line.id}
                </div>
                <input
                  type="text"
                  value={line.name || ''}
                  onChange={(e) => updateLineName(line.id, e.target.value)}
                  placeholder={`Line ${line.id} Name`}
                  className="flex-1 px-2 py-1 border border-ok-border rounded text-sm"
                />
                <button
                  onClick={() => removeLine(line.id)}
                  className="p-1 text-danger-fg hover:text-danger-fg hover:bg-danger-bg rounded"
                  title="Remove this line"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            
            {semester2Lines.length === 0 && (
              <div className="text-center py-4 text-ink-500">
                No Semester 2 lines. Click "Add S2 Line" to create one.
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NewLineManagementModal;